import { Component, ViewEncapsulation, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, ValidatorFn, AbstractControl, ValidationErrors, FormArray } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { RickService } from '../service/rick.service';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-inventory-adjustments',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule, MatExpansionModule],
  templateUrl: './inventory-adjustments.component.html',
  styleUrl: './inventory-adjustments.component.scss',
  providers: [RickService]
})
export class InventoryAdjustmentsComponent {
  @ViewChild('errorMessageDiv') errorDiv!: ElementRef;
  @ViewChild('successMessageDiv') successDiv!: ElementRef;
  inventoryAdjForm: FormGroup = new FormGroup({});
  inventoryAdjLotListForm: FormGroup = new FormGroup({});
  submitted: boolean = false;
  isSaved: boolean = false;
  lotList: any[] = [];
  customer: any = '';
  state: any = '';
  store: any = '';
  isInventoryFormSubmitted: boolean = false;
  isLotDataReady: boolean = false;
  reasonCodes: Array<any> = [{
    name: '(+02) - Found Inventory',
    value: '02'
  },
  {
    name: '(-09) - Return/Damage/Lost',
    value: '09'
  }];

  constructor(private fb: FormBuilder, private router: Router, private rickService: RickService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.inventoryAdjForm = this.fb.group({
      customer: ['', Validators.required],
      state: ['', Validators.required],
      store: ['', Validators.required],
    });

    this.inventoryAdjLotListForm = this.fb.group({
      lotListArray: this.fb.array([this.lotGroupDetails()])
    });
  }

  lotGroupDetails() {
    return this.fb.group({
      lotNumber: [''],
      inventoryMasterReassignRecordDtoList: this.fb.array([this.sizeDetailsGroup()])
    });
  }

  sizeDetailsGroup() {
    return this.fb.group({
      dim1: [''],
      dim2: [''],
      qty: ['', [
        Validators.pattern("^[0-9]*$")
      ]],
      reasonCode: [''],
    },{ validator: this.compareDates('qty', 'reasonCode') });
  }

  compareDates(qty: any, reasonCode: string) {
    return (formGroup: FormGroup<any>) => {
      const qtyControl = formGroup.controls[qty];
      const reasonCodeControl = formGroup.controls[reasonCode];

      if (!qtyControl.value && !reasonCodeControl.value) {
        return null;
      }

      if (qtyControl.errors && !qtyControl.errors.qtyReasonCodeRequired) {
        return null;
      }
      if ((qtyControl.value != '' && !reasonCodeControl.value) || (!qtyControl.value && reasonCodeControl.value != '')) {
        return qtyControl.setErrors({ qtyReasonCodeRequired: true });
      } else {
        return qtyControl.setErrors(null);
      }
    }
  }

  checkSizeDetailsErrors(field: string, errorType: string, lotIndex: number, dimensionIndex: number) {
    return (this.submitted &&
      ((((this.inventoryAdjLotListForm.controls["lotListArray"] as FormArray).controls[lotIndex] as FormGroup)
        .controls['inventoryMasterReassignRecordDtoList'] as FormArray).controls[dimensionIndex] as FormGroup).controls[field].errors?.[errorType]) ? true : false;
  }

  getGroup(index: number) {
    return (this.inventoryAdjLotListForm.controls["lotListArray"] as FormArray).at(index) as FormGroup;
  }

  setupSizeDetailsData(data: any, indx: any) {
    let sizeDetailsArray: any = [];
    data.forEach((element: any, index: number) => {
      sizeDetailsArray.push({
        "dim1": element.dim1,
        "dim2": element.dim2
      });
      if (index != 0) {
        this.addSizeDetails(indx);
      }
    });
    (((this.inventoryAdjLotListForm.controls["lotListArray"] as FormArray).controls[indx] as FormGroup)
      .controls['inventoryMasterReassignRecordDtoList'] as FormArray).patchValue(sizeDetailsArray);
  }

  addSizeDetails(i: any) {
    const sizeDetailsForm: FormGroup = this.sizeDetailsGroup();
    (((this.inventoryAdjLotListForm.controls["lotListArray"] as FormArray).controls[i] as FormGroup)
      .controls['inventoryMasterReassignRecordDtoList'] as FormArray).push(sizeDetailsForm);
  }

  setupLotGroupList() {
    let lotGroupArray: any = [];
    this.lotList.forEach((element: any, index: number) => {
      if (index != 0) {
        this.addLotGroupDetails();
      }
      lotGroupArray.push({
        "lotNumber": element.lotNumber,
        "inventoryMasterReassignRecordDtoList": this.setupSizeDetailsData(element.inventoryMasterReassignRecordDtoList, index)
      });
    });
    (this.inventoryAdjLotListForm.controls["lotListArray"] as FormArray).patchValue(lotGroupArray);
  }

  addLotGroupDetails() {
    const lotGroupDetailsForm: FormGroup = this.lotGroupDetails();
    (this.inventoryAdjLotListForm.controls["lotListArray"] as FormArray).push(lotGroupDetailsForm);
  }

  fetchLotIds() {
    this.submitted = true;
    if (this.inventoryAdjForm.valid) {
      this.customer = this.inventoryAdjForm.controls.customer.value;
      this.state = this.inventoryAdjForm.controls.state.value;
      this.store = this.inventoryAdjForm.controls.store.value;

      this.rickService.fetchLotsByEDPInventoryAdj(this.customer, this.state, this.store).subscribe((res) => {
        this.setupLotlistDetails(res);
      }, (error) => {
        this.inventoryAdjForm?.setErrors({ invalid: error?.error?.errorMessages[0] });
        this.changeDetectorRef.detectChanges();
        this.errorDiv.nativeElement.scrollIntoView();
      });
    }
  }

  setupLotlistDetails(data: any) {
    this.lotList = data?.lotNumberInventoryMasterListDtoList;
    this.setupLotGroupList();
    this.isLotDataReady = true;
  }

  cancelPage() {
    this.router.navigate(['/home']);
  }

  updateInventoryAdjustments() {
    if (this.inventoryAdjLotListForm.valid) {
      this.isInventoryFormSubmitted = true;
      let postData = {
        "edpNumberDto": {
          "customer": this.customer,
          "state": this.state,
          "store": this.store
        },
        "lotNumberInventoryMasterListDtoList": this.inventoryAdjLotListForm.controls['lotListArray'].value
      };
        this.rickService.updateInventoryAdj(postData).subscribe((res) => {
          this.isSaved = true;
          this.changeDetectorRef.detectChanges();
          this.errorDiv.nativeElement.scrollIntoView();
        }, (error) => {
          this.inventoryAdjLotListForm?.setErrors({ invalid: error?.error?.errorMessages[0] });
          this.changeDetectorRef.detectChanges();
          this.errorDiv.nativeElement.scrollIntoView();
        });
    }
  }
}
